import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { previewPrompt } from '@/services/admin-api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface PromptPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  promptId: string | null;
  version?: number;
}

export function PromptPreviewDialog({
  open,
  onOpenChange,
  promptId,
  version,
}: PromptPreviewDialogProps) {
  const { toast } = useToast();
  const [variablesJson, setVariablesJson] = useState('{}');
  const [result, setResult] = useState<{
    systemPrompt: string;
    userPrompt?: string;
  } | null>(null);

  const previewMutation = useMutation({
    mutationFn: () => {
      let variables: Record<string, unknown>;
      try {
        variables = JSON.parse(variablesJson);
      } catch {
        throw new Error('Invalid JSON for variables');
      }
      return previewPrompt(promptId!, { version, variables });
    },
    onSuccess: (data) => {
      setResult({
        systemPrompt: data.data.systemPrompt,
        userPrompt: data.data.userPrompt,
      });
    },
    onError: (error: Error) => {
      toast({ title: 'Preview failed', description: error.message, variant: 'destructive' });
    },
  });

  const handleClose = () => {
    onOpenChange(false);
    setResult(null);
    setVariablesJson('{}');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview Prompt</DialogTitle>
          <DialogDescription>
            Preview "{promptId}" with template variables.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="preview-vars">Variables (JSON)</Label>
            <Textarea
              id="preview-vars"
              className="font-mono text-sm min-h-[80px]"
              value={variablesJson}
              onChange={(e) => setVariablesJson(e.target.value)}
              placeholder='{"resumeText": "...", "jobDescription": "..."}'
            />
          </div>

          <Button
            onClick={() => previewMutation.mutate()}
            disabled={previewMutation.isPending || !promptId}
          >
            {previewMutation.isPending ? 'Rendering...' : 'Render Preview'}
          </Button>

          {result && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Rendered System Prompt</Label>
                <div className="bg-muted p-4 rounded font-mono text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {result.systemPrompt}
                </div>
              </div>
              {result.userPrompt && (
                <div className="space-y-2">
                  <Label>Rendered User Prompt</Label>
                  <div className="bg-muted p-4 rounded font-mono text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                    {result.userPrompt}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
